import FlightLandIcon from '@mui/icons-material/FlightLand';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Box, Tooltip, Typography, styled } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DateTime } from 'luxon';
import { drawerWidth } from 'materialUI/jss/material-dashboard-pro-react';
import { useBookingWizardContext } from '../hooks/useBookingWizard';

const SIDEPANEL_WIDTH = 552
const SIDEPANEL_GUTTER = 16
const MAP_LEFT_EDGE =
  drawerWidth + SIDEPANEL_GUTTER + SIDEPANEL_WIDTH + SIDEPANEL_GUTTER

const Banner = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 90,
  left: `calc(${MAP_LEFT_EDGE}px + (100vw - ${MAP_LEFT_EDGE}px) / 2)`,
  transform: 'translateX(-50%)',
  zIndex: 75,
  width: 'min(720px, calc(100vw - ' + (MAP_LEFT_EDGE + 32) + 'px))',
  display: 'flex',
  alignItems: 'flex-start',
  gap: 12,
  padding: '12px 16px 12px 14px',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.common.white,
  border: `1px solid ${alpha(theme.palette.brand?.dark || '#0a253d', 0.08)}`,
  boxShadow: theme.shadows[6],
}))

const IconBubble = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  width: 36,
  height: 36,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: alpha(theme.palette.secondary.main, 0.12),
  color: theme.palette.secondary.main,
}))

const TextColumn = styled(Box)({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
})

const Title = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  fontWeight: 700,
  lineHeight: 1.25,
  color: theme.palette.brand?.dark || theme.palette.text.primary,
}))

const Subtitle = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  lineHeight: 1.35,
  color: theme.palette.text.secondary,
  '& b': {
    color: theme.palette.brand?.dark || theme.palette.text.primary,
    fontWeight: 600,
  },
}))

const WhyButton = styled('button')(({ theme }) => ({
  flexShrink: 0,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  background: 'transparent',
  border: `1px solid ${alpha(theme.palette.text.secondary, 0.3)}`,
  color: theme.palette.text.secondary,
  borderRadius: 999,
  padding: '2px 8px',
  fontSize: 11,
  fontWeight: 600,
  fontFamily: theme.typography.fontFamily,
  cursor: 'help',
  '&:hover': {
    borderColor: theme.palette.text.primary,
    color: theme.palette.text.primary,
  },
  '& > svg': { fontSize: 13 },
}))

const WHY_EXPLANATION =
  "We used the flight details from the booking you just placed to find ground-transport options waiting at your arrival airport - drawn from places you've been before, your team's favourites, and popular picks from this airport."

const formatLocalArrival = (iso: string | undefined): string | null => {
  if (!iso) return null
  const dt = DateTime.fromISO(iso, { setZone: true })
  if (!dt.isValid) return null
  return dt.toFormat('HH:mm')
}

const OnwardMapOverlay: React.FC = () => {
  const wizard = useBookingWizardContext()
  const { onwardSuggestions, confirmedBookingId, mapFlightArc } = wizard

  if (!confirmedBookingId) return null
  if (!onwardSuggestions.length) return null

  const first = onwardSuggestions[0]
  const arrivalAirport = first?.arrivalAirport
  const iata =
    arrivalAirport?.iata || mapFlightArc?.to?.iata || 'your destination'
  const flightNumber = first?.flightNumber
  const arrivalTime = formatLocalArrival(first?.arrivalDateTime)

  return (
    <Banner role="status">
      <IconBubble>
        <FlightLandIcon sx={{ fontSize: 20 }} />
      </IconBubble>
      <TextColumn>
        <Title>
          Landing at {iata}? We&rsquo;ve got your onward ride sorted.
        </Title>
        <Subtitle>
          {flightNumber ? (
            <>
              Your flight <b>{flightNumber}</b>
              {arrivalTime ? (
                <>
                  {' '}
                  lands at{' '}
                  <b>
                    {iata} around {arrivalTime}
                  </b>
                </>
              ) : (
                <>
                  {' '}
                  lands at <b>{iata}</b>
                </>
              )}{' '}
              - here are cars ready when you touch down.
            </>
          ) : (
            <>
              Here are cars ready when you touch down at <b>{iata}</b>.
            </>
          )}
        </Subtitle>
      </TextColumn>
      <Tooltip title={WHY_EXPLANATION} arrow placement="bottom-end">
        <WhyButton type="button" aria-label="Why am I seeing these?">
          <HelpOutlineIcon />
          Why this?
        </WhyButton>
      </Tooltip>
    </Banner>
  )
}

export default OnwardMapOverlay
